<template>
  <div class="settings-panel">
    <!-- 步驟二引導 -->
    <GuideStep2 ref="guideStep2Ref" :activity-count="activityCount" />

    <!-- 標題列 -->
    <div class="settings-header">
      <button class="back-btn" @click="handleBack" ref="backButtonRef">
        <svg-icon icon-class="arrow-pre" />
      </button>
      <h3 class="settings-title">{{ displayTitle }} - {{ t('gameActManager.gameSettingsEntry') }}</h3>
    </div>

    <!-- 設定內容 -->
    <div class="settings-body">
      <!-- 自定義遊戲專用佈局 -->
      <div v-if="questionType === 'aiGenerated'" class="custom-game-layout">
        <!-- 左側：程式碼輸入區 -->
        <div class="code-editor-section">
          <div class="section-header">
            <label class="section-title">{{ t('gameActManager.customGameCode') }}</label>
            <button class="btn-run" @click="handlePreview">Run</button>
          </div>

          <!-- 遊戲參數設定（程式碼輸入） -->
          <GameParametersForm
            ref="gameParametersFormRef"
            :gameType="'aigame'"
            :activity-name="props.activityName"
            :player-count="1"
            v-model="gameParameters"
          />
        </div>

        <!-- 右側：即時預覽區 -->
        <div class="preview-section">
          <div class="preview-container">
            <iframe
              ref="previewIframe"
              :srcdoc="previewHtml"
              sandbox="allow-scripts"
              class="preview-iframe"
            ></iframe>
          </div>
        </div>
      </div>

      <!-- 一般遊戲佈局 -->
      <div v-else class="settings-layout">
        <!-- 左側區域 -->
        <div class="settings-left">
          <!-- 題型標籤 -->
          <div class="setting-group">
            <label class="setting-label">{{ t('gameActManager.questionType') }}</label>
            <span class="type-badge">{{ questionTypeLabel }}</span>
          </div>

          <!-- 遊戲人數 (自定義遊戲不顯示) -->
          <div class="setting-group" v-if="questionType !== 'aiGenerated'">
            <label class="setting-label">{{ t('gameActManager.playerCount') }}</label>
            <div class="radio-group">
              <label class="radio-item">
                <input
                  type="radio"
                  name="playerCount"
                  :value="1"
                  v-model="localSettings.playerCount"
                  :disabled="questionType === 'multiChoice'"
                />
                <span class="radio-text" :class="{ 'disabled': questionType === 'multiChoice' }">1 {{ t('gameActManager.person') }}</span>
              </label>
              <label class="radio-item" v-if="questionType !== 'multiChoice'">
                <input
                  type="radio"
                  name="playerCount"
                  :value="2"
                  v-model="localSettings.playerCount"
                />
                <span class="radio-text">2 {{ t('gameActManager.person') }}</span>
              </label>
            </div>
          </div>

          <!-- 預設遊戲模板 (自定義遊戲不顯示) -->
          <div class="setting-group" v-if="questionType !== 'aiGenerated'">
            <label class="setting-label">{{ t('gameActManager.defaultTemplate') }}</label>
            <div class="game-template-grid">
              <div
                v-for="option in gameOptions"
                :key="option.value"
                class="game-template-card"
                :class="{ 'active': option.value === localSettings.currentGame }"
                @click="selectOption(option.value)"
              >
                <div class="game-card-image">
                  <img :src="getGameImage(option.value)" :alt="option.label">
                </div>
                <div class="game-card-name">{{ option.label }}</div>
              </div>
            </div>
          </div>

          <!-- 允許變換遊戲模板 (自定義遊戲不顯示) -->
          <div class="setting-group allow-change-row" v-if="questionType !== 'aiGenerated'">
            <label class="setting-label" :class="{ 'disabled': isAllowTemplateChangeDisabled }">
              {{ t('gameActManager.allowTemplateChange') }}
            </label>
            <div class="toggle-switch">
              <input
                type="checkbox"
                id="allowTemplateChange"
                v-model="localSettings.allowTemplateChange"
                :disabled="isAllowTemplateChangeDisabled"
                class="toggle-input"
              />
              <label for="allowTemplateChange" class="toggle-label" :class="{ 'disabled': isAllowTemplateChangeDisabled }"></label>
            </div>
          </div>

          <!-- 遊戲預覽按鈕 -->
          <div class="setting-group">
            <button class="btn-preview" @click="handlePreview">
              <svg-icon icon-class="eye" class="preview-icon" />
              <span>{{ t('gameActManager.gamePreview') }}</span>
            </button>
          </div>

          <!-- HT 名單匯入區（WebView 模式或有現有名單時顯示） -->
          <div v-if="isWebview || hasExistingRoster || useHiTeachRoster" class="setting-group roster-section">
            <!-- 勾選框與重新整理按鈕 -->
            <div class="roster-header">
              <label class="checkbox-container" :class="{ 'disabled': !isWebview }">
                <input
                  type="checkbox"
                  ref="rosterCheckboxRef"
                  :checked="useHiTeachRoster"
                  class="roster-checkbox"
                  @change="handleRosterCheckboxChange"
                />
                <span class="checkbox-label">
                  {{ t('gameActManager.useHiTeachRoster') }}
                </span>
              </label>

              <!-- 重新請求目前名單按鈕（僅在 WebView 模式下顯示） -->
              <button
                v-if="useHiTeachRoster && isWebview"
                class="btn-refresh-roster"
                @click="handleRefreshRoster"
              >
                <svg-icon icon-class="refresh" />
                <span>{{ t('gameActManager.requestCurrentRoster') }}</span>
              </button>
            </div>

            <!-- 名單顯示區域 -->
            <div v-if="useHiTeachRoster && memberList.length > 0" class="roster-display">
              <div class="roster-title">
                {{ t('gameActManager.studentList') }}
                <span class="student-count">({{ memberList.length }} {{ t('gameActManager.students') }})</span>
              </div>

              <div class="roster-table-wrapper">
                <table class="roster-table">
                  <thead>
                    <tr>
                      <th>{{ t('gameActManager.seatNo') }}</th>
                      <th>{{ t('gameActManager.studentName') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(student, index) in memberList" :key="`student-${index}-${student.seatID || student.seatNo || index}`">
                      <td>{{ student.seatID || student.seatNo || student.seatNumber || '-' }}</td>
                      <td>{{ student.memberName || student.name || student.studentName || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 勾選但名單為空時的提示 -->
            <div v-if="useHiTeachRoster && memberList.length === 0" class="roster-empty">
              <svg-icon icon-class="info" class="info-icon" />
              <p>{{ t('gameActManager.rosterEmptyHint') }}</p>
              <button v-if="isWebview" class="btn-refresh-roster-secondary" @click="handleRefreshRoster">
                {{ t('gameActManager.clickToRefresh') }}
              </button>
            </div>
          </div>
        </div>

        <!-- 右側區域 -->
        <div class="settings-right">
          <!-- 遊戲參數設定 -->
          <GameParametersForm
            ref="gameParametersFormRef"
            :gameType="localSettings.currentGame"
            :activity-name="props.activityName"
            :player-count="localSettings.playerCount"
            v-model="gameParameters"
          />
        </div>
      </div>
    </div>

    <!-- 自製遊戲安全檢查警告 Modal -->
    <div v-if="showSecurityWarningModal" class="modal-overlay">
      <div class="modal-content delete-confirm-modal security-warning-modal">
        <div class="modal-header">
          <h3>{{ t('gameActManager.securityCheck.title') }}</h3>
          <button class="close-btn" @click="closeSecurityWarningModal">
            <svg-icon icon-class="close" />
          </button>
        </div>
        <div class="modal-body security-warning-body">
          <div v-if="securityErrors.length" class="security-section">
            <h4 class="section-title">{{ t('gameActManager.securityCheck.errorTitle') }}</h4>
            <ul class="issue-list error-list">
              <li v-for="(issue, index) in securityErrors" :key="'err-' + index">{{ index + 1 }}. {{ issue.message }}</li>
            </ul>
          </div>

          <div v-if="securityWarnings.length" class="security-section">
            <h4 class="section-title">{{ t('gameActManager.securityCheck.warningTitle') }}</h4>
            <ul class="issue-list warning-list">
              <li v-for="(issue, index) in securityWarnings" :key="'warn-' + index">{{ index + 1 }}. {{ issue.message }}</li>
            </ul>
          </div>

          <div class="security-section suggestions">
            <h4 class="section-title">{{ t('gameActManager.securityCheck.suggestionTitle') }}</h4>
            <ul class="issue-list suggestion-list">
              <li>• {{ t('gameActManager.securityCheck.suggestion1') }}</li>
              <li>• {{ t('gameActManager.securityCheck.suggestion2') }}</li>
              <li>• {{ t('gameActManager.securityCheck.suggestion3') }}</li>
            </ul>
            <p class="continue-text">{{ t('gameActManager.securityCheck.continuePreview') }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="closeSecurityWarningModal">
            {{ t('gameActManager.cancel') }}
          </button>
          <button type="button" class="delete-btn" @click="closeSecurityWarningModal">
            {{ t('gameActManager.confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 非 WebView 取消 HT 名單勾選警告 Modal -->
    <div v-if="showRosterUncheckConfirm" class="modal-overlay">
      <div class="modal-content delete-confirm-modal roster-uncheck-modal">
        <div class="modal-header">
          <h3>{{ t('gameActManager.rosterUncheckConfirmTitle') }}</h3>
          <button class="close-btn" @click="cancelRosterUncheck">
            <svg-icon icon-class="close" />
          </button>
        </div>
        <div class="modal-body">
          <p class="warning-text danger-color">{{ t('gameActManager.rosterUncheckConfirmMessage') }}</p>
         
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="cancelRosterUncheck">
            {{ t('gameActManager.cancel') }}
          </button>
          <button type="button" class="delete-btn" @click="confirmRosterUncheck">
            {{ t('gameActManager.confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 重新請求目前名單確認 Modal -->
    <div v-if="showRefreshRosterConfirm" class="modal-overlay">
      <div class="modal-content delete-confirm-modal refresh-roster-modal">
        <div class="modal-header">
          <h3>{{ t('gameActManager.requestCurrentRoster') }}</h3>
          <button class="close-btn" @click="cancelRefreshRoster">
            <svg-icon icon-class="close" />
          </button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">{{ t('gameActManager.confirmRefreshRoster') }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="cancelRefreshRoster">
            {{ t('gameActManager.cancel') }}
          </button>
          <button type="button" class="delete-btn" @click="confirmRefreshRoster">
            {{ t('gameActManager.confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 遊戲預覽 iframe 彈窗 (僅 WebView 環境) -->
    <div v-if="showGamePreviewModal" class="modal-overlay game-preview-overlay">
      <div class="game-preview-modal">
        <!-- iframe 直接撐滿整個彈窗 -->
        <iframe
          :src="gamePreviewUrl"
          class="game-preview-iframe"
          frameborder="0"
          allowfullscreen
          sandbox="allow-scripts allow-same-origin"
          referrerpolicy="strict-origin-when-cross-origin"
        ></iframe>

        <!-- 標題欄浮動在 iframe 上方 -->
        <div class="preview-modal-header-overlay">
          <h3>{{ t('gameActManager.gamePreview') }}</h3>
          <button class="close-btn" @click="closeGamePreviewModal">
            <svg-icon icon-class="close" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { reactive, watch, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import SvgIcon from '../../components/SvgIcon.vue';
import GameParametersForm from './GameParametersForm.vue';
import GuideStep2 from './GuideStep2.vue';
import { sanitizeGameHTML } from '@/utils/gameSanitizer';
import { useGameMessageBridge } from '@/composable/useGameMessageBridge';
import { Message } from 'view-ui-plus';

const { t } = useI18n();

// 引導組件 ref
const guideStep2Ref = ref(null);
const backButtonRef = ref(null);

// Props
const props = defineProps({
  settings: {
    type: Object,
    required: true
  },
  activityName: {
    type: String,
    required: false,
    default: ''
  },
  questionType: {
    type: String,
    required: true
  },
  questionTypeLabel: {
    type: String,
    required: true
  },
  activityCount: {
    type: Number,
    default: 0
  }
});

// Emits
const emit = defineEmits(['back', 'update']);

// 本地設定（避免直接修改 props）
const localSettings = reactive({
  playerCount: props.settings.playerCount || 1,
  currentGame: props.questionType === 'trueFalse'
    ? 'tf'
    : props.questionType === 'multiChoice'
    ? 'maze'
    : (props.settings.currentGame || 'mole'),
  allowTemplateChange: props.questionType === 'trueFalse' || props.questionType === 'multiChoice'
    ? false
    : (props.settings.allowTemplateChange || false)
});

// 遊戲參數設定（從 props 初始化，包含 topic）
const gameParameters = ref(props.settings.gameParameters || {});

// GameParametersForm 組件的引用
const gameParametersFormRef = ref(null);

// ========== HT 名單匯入功能 ==========

// WebView 環境判斷
const isWebview = computed(() => {
  return window?.chrome?.webview !== undefined;
});

// HT 名單相關狀態
const useHiTeachRoster = ref(false); // 勾選框狀態
const memberList = ref([]); // 本地顯示的學生名單
const isLoadingRoster = ref(false); // 載入中狀態
const rosterCheckboxRef = ref(null); // checkbox DOM 引用
const showRefreshRosterConfirm = ref(false); // 重新請求名單確認 Modal

// 判斷是否有現有名單（來自 DB 的設定）
const hasExistingRoster = computed(() => {
  // 檢查 1: 當前 memberList 是否有數據（最直接）
  if (memberList.value && memberList.value.length > 0) {
    return true;
  }

  // 檢查 2: props.settings.gameParameters（原始數據源，DB 回傳）
  if (props.settings?.gameParameters?.classRosterSource === 'HT' && props.settings?.gameParameters?.classRoster) {
    try {
      const list = JSON.parse(props.settings.gameParameters.classRoster);
      if (Array.isArray(list) && list.length > 0) {
        return true;
      }
    } catch (e) {
      // 靜默處理錯誤
    }
  }

  // 檢查 3: gameParameters 中是否已有 HT 名單（本地編輯狀態）
  if (gameParameters.value?.classRosterSource === 'HT' && gameParameters.value?.classRoster) {
    try {
      const list = JSON.parse(gameParameters.value.classRoster);
      if (Array.isArray(list) && list.length > 0) {
        return true;
      }
    } catch (e) {
      // 靜默處理錯誤
    }
  }

  return false;
});

// HT 名單相關方法

/**
 * 從 sessionStorage 載入名單
 * @param {boolean} showMessages - 是否顯示訊息提示，預設為 true
 */
const loadMemberListFromSession = (showMessages = true) => {
  try {
    const memberListStr = sessionStorage.getItem('ht_memberList');

    if (memberListStr) {
      const parsedList = JSON.parse(memberListStr);

      if (Array.isArray(parsedList) && parsedList.length > 0) {
        memberList.value = parsedList;
        console.log(`[GameSettings] 載入 HiTeach 名單成功 (${parsedList.length} 位學生)`);
      } else {
        memberList.value = [];
        console.warn('[GameSettings] HiTeach 名單為空陣列');

        if (showMessages) {
          Message.info({
            content: t('gameActManager.rosterEmptyHint'),
            duration: 4
          });
        }
      }
    } else {
      memberList.value = [];
      console.warn('[GameSettings] sessionStorage 中無 ht_memberList');

      if (showMessages) {
        Message.warning({
          content: t('gameActManager.rosterNotFound'),
          duration: 4
        });
      }
    }
  } catch (error) {
    console.error('[GameSettings] 解析 memberList 失敗:', error);
    memberList.value = [];

    if (showMessages) {
      Message.error({
        content: t('gameActManager.rosterLoadError'),
        duration: 4
      });
    }
  }
};

/**
 * 清空名單
 */
const clearMemberList = () => {
  memberList.value = [];
  console.log('[GameSettings] 已清空名單');
};

/**
 * 處理「使用 HT 匯入名單」勾選框變更
 * - WebView：行為與原本相同
 * - 非 WebView：
 *    - 不允許重新勾選（名單來源只能在 HT 內設置）
 *    - 取消勾選時彈出警告 modal，確認後才清空名單
 */
const showRosterUncheckConfirm = ref(false);

const handleRosterCheckboxChange = (event) => {
  const checked = event.target.checked;

  // 非 WebView 環境
  if (!isWebview.value) {
    if (checked) {
      // 不允許在瀏覽器重新勾選名單來源
      Message.warning({
        content: t('gameActManager.rosterSourceOnlyInHT'),
        duration: 4
      });

      // 還原為未勾選狀態
      event.target.checked = false;
      return;
    } else {
      // 取消勾選：若目前有名單，先顯示確認 modal
      if (memberList.value.length > 0) {
        showRosterUncheckConfirm.value = true;
        // 保持 checkbox 為勾選狀態，待使用者確認後再真正取消
        event.target.checked = true;
        return;
      }

      // 沒有名單直接清空狀態
      useHiTeachRoster.value = false;
      clearMemberList();
      return;
    }
  }

  // WebView 環境：維持原有行為
  useHiTeachRoster.value = checked;
  if (checked) {
    console.log('[GameSettings] 啟用 HT 名單顯示（不從 session 重新載入）');
  } else {
    clearMemberList();
  }
};

// 非 WebView 取消勾選名單確認 Modal 操作
const cancelRosterUncheck = () => {
  showRosterUncheckConfirm.value = false;
  // 還原 checkbox 為勾選狀態
  if (rosterCheckboxRef.value) {
    rosterCheckboxRef.value.checked = true;
  }
};

const confirmRosterUncheck = () => {
  showRosterUncheckConfirm.value = false;
  useHiTeachRoster.value = false;
  clearMemberList();
  if (rosterCheckboxRef.value) {
    rosterCheckboxRef.value.checked = false;
  }
};

/**
 * 從 HiTeach 重新請求目前名單
 */
const refreshRosterFromHiTeach = () => {
  try {
    isLoadingRoster.value = true;

    // 顯示 loading 訊息
    Message.loading({
      content: t('gameActManager.requestingRoster'),
      duration: 0
    });

    // 發送 Get.GameInitInfo 訊息
    messageBridge.postMessage({
      action: 'Get.GameInitInfo'
    });

    console.log('[GameSettings] 已發送 Get.GameInitInfo 請求');

    // 設定超時處理（10秒）
    setTimeout(() => {
      if (isLoadingRoster.value) {
        isLoadingRoster.value = false;
        Message.destroy();
        Message.warning({
          content: t('gameActManager.refreshRosterTimeout'),
          duration: 4
        });
      }
    }, 10000);

  } catch (error) {
    console.error('[GameSettings] 發送 Get.GameInitInfo 失敗:', error);
    isLoadingRoster.value = false;

    Message.error({
      content: t('gameActManager.refreshRosterError'),
      duration: 4
    });
  }
};

/**
 * 處理「重新請求目前名單」按鈕點擊
 */
const handleRefreshRoster = () => {
  // 改用自訂 Modal 進行確認
  showRefreshRosterConfirm.value = true;
};

// 重新請求名單確認 Modal 操作
const cancelRefreshRoster = () => {
  showRefreshRosterConfirm.value = false;
};

const confirmRefreshRoster = () => {
  showRefreshRosterConfirm.value = false;
  refreshRosterFromHiTeach();
};

/**
 * Init.GameHome 回調處理（傳給 useGameMessageBridge）
 */
const onInitGameHome = (data) => {
  console.log('[GameSettings] 收到 Init.GameHome 回應:', data);

  // setupUserAuth 已經處理了 memberList 的儲存
  // 我們只需要重新載入
  setTimeout(() => {
    loadMemberListFromSession();

    isLoadingRoster.value = false;
    Message.destroy(); // 關閉 loading

    // 顯示成功訊息
    if (memberList.value.length > 0) {
      Message.success({
        content: t('gameActManager.rosterRefreshSuccess', { count: memberList.value.length }),
        duration: 3
      });
    } else {
      Message.warning({
        content: t('gameActManager.rosterStillEmpty'),
        duration: 4
      });
    }
  }, 100);
};

// 初始化 messageBridge（僅在 WebView 模式）
let messageBridge = null;
if (isWebview.value) {
  const { messageBridge: bridge } = useGameMessageBridge('GameSettings', onInitGameHome);
  messageBridge = bridge;
}

// iframe 預覽相關
const previewIframe = ref(null);
const previewHtml = ref('');

// 自製遊戲安全檢查 Modal 狀態
const showSecurityWarningModal = ref(false);
const securityErrors = ref([]);
const securityWarnings = ref([]);

// 遊戲預覽 iframe 彈窗狀態 (WebView 專用)
const showGamePreviewModal = ref(false);
const gamePreviewUrl = ref('');

const openSecurityWarningModal = (errors, warnings) => {
  securityErrors.value = errors || [];
  securityWarnings.value = warnings || [];
  showSecurityWarningModal.value = true;
};

const closeSecurityWarningModal = () => {
  showSecurityWarningModal.value = false;
};

// 遊戲預覽 iframe 彈窗操作
const openGamePreviewModal = (url) => {
  gamePreviewUrl.value = url;
  showGamePreviewModal.value = true;
};

const closeGamePreviewModal = () => {
  showGamePreviewModal.value = false;
  gamePreviewUrl.value = '';
};

// 監聽變化並向上傳遞
watch([localSettings, gameParameters], ([settings, params]) => {
  emit('update', {
    ...settings,
    gameParameters: params
  });
}, { deep: true });

// 監聽 useHiTeachRoster 和 memberList 變化，自動更新 gameParameters
watch([useHiTeachRoster, memberList], ([useRoster, roster]) => {
  console.log('[GameSettings] 名單狀態變更:', {
    useRoster,
    rosterLength: roster.length,
    rosterData: roster
  });

  if (useRoster && roster.length > 0) {
    // 勾選且有名單：直接序列化陣列
    gameParameters.value = {
      ...gameParameters.value,
      classRosterSource: 'HT',
      classRoster: JSON.stringify(roster)
    };

    console.log(`[GameSettings] 已設定 HT 名單 (${roster.length} 位學生)`, {
      classRosterSource: 'HT',
      classRosterLength: roster.length
    });
  } else {
    // 未勾選或名單為空：設置為 general 模式，空陣列
    gameParameters.value = {
      ...gameParameters.value,
      classRosterSource: 'general',
      classRoster: JSON.stringify([])
    };

    console.log('[GameSettings] 已設定為一般模式（無名單）', {
      classRosterSource: 'general',
      classRosterLength: 0
    });
  }
}, { deep: true });

// 計算屬性：取得當前顯示的標題名稱（優先使用 gameParameters 中的 topic）
const displayTitle = computed(() => {
  return gameParameters.value?.topic || props.activityName || '';
});

// 遊戲選項（根據題型和遊戲人數過濾）
const gameOptions = computed(() => {
  // 是非題：固定顯示火箭升空（不受人數影響）
  if (props.questionType === 'trueFalse') {
    return [{ value: 'tf', label: t('gameActManager.rocketGame') }];
  }

  // 單選題：固定顯示迷宮
  if (props.questionType === 'multiChoice') {
    return [{ value: 'maze', label: t('gameActManager.mazeGame') }];
  }

  // 辨識題：根據人數過濾
  if (localSettings.playerCount === 2) {
    // 2 人顯示摘星星和貪食蛇
    return [
      { value: 'star', label: t('gameActManager.starGame') },
      { value: 'snake', label: t('gameActManager.snakeGame') }
    ];
  }
  // 1 人顯示全部選項
  return [
    { value: 'mole', label: t('gameActManager.moleGame') },
    { value: 'star', label: t('gameActManager.starGame') },
    { value: 'snake', label: t('gameActManager.snakeGame') }
  ];
});

// 是否禁用「允許變換遊戲模板」（2人時或是非題、單選題時禁用）
const isAllowTemplateChangeDisabled = computed(() => {
  return localSettings.playerCount === 2 || props.questionType === 'trueFalse' || props.questionType === 'multiChoice';
});

// 監聽遊戲人數變化，自動調整遊戲模板
watch(() => localSettings.playerCount, (newCount) => {
  // 是非題：固定為火箭升空，不因人數變化而改變
  if (props.questionType === 'trueFalse') {
    localSettings.currentGame = 'tf';
    localSettings.allowTemplateChange = false;
    return;
  }

  // 單選題：固定為迷宮，不因人數變化而改變
  if (props.questionType === 'multiChoice') {
    localSettings.currentGame = 'maze';
    localSettings.allowTemplateChange = false;
    return;
  }

  // 辨識題：2人時，如果當前是打地鼠（不支援雙人），則切換到摘星星
  if (newCount === 2) {
    if (localSettings.currentGame === 'mole') {
      localSettings.currentGame = 'star';
    }
    localSettings.allowTemplateChange = false;
  }
});

// 選擇選項
const selectOption = (value) => {
  localSettings.currentGame = value;
};

// 取得遊戲圖片
const getGameImage = (gameType) => {
  const images = {
    'mole': require('@/assets/HTwbActAssets/home/cover-molegame.webp'),
    'star': require('@/assets/HTwbActAssets/home/cover-pvpgame.webp'),
    'tf': require('@/assets/HTwbActAssets/home/cover-tfgame.webp'),
    'snake': require('@/assets/HTwbActAssets/home/cover-snakegame.webp'),
    'maze': require('@/assets/HTwbActAssets/home/cover-mazegame.webp'),
    'aigame': require('@/assets/HTwbActAssets/home/cover-aigame.webp')
  };
  return images[gameType] || images['mole'];
};

// 名單初始化邏輯（可重複呼叫，支援切換活動時重新載入）
// 名單來源一律來自 DB（props.settings / gameParameters），不從 sessionStorage 讀取
const initRoster = () => {
  let rosterLoaded = false;

  console.log('[GameSettings] 檢查數據來源...', {
    propsHasRoster: props.settings?.gameParameters?.classRosterSource === 'HT',
    gameParamsHasRoster: gameParameters.value?.classRosterSource === 'HT'
  });

  // 檢查 1: 從 props.settings.gameParameters 載入（DB 回傳資料）
  if (props.settings?.gameParameters?.classRosterSource === 'HT' && props.settings?.gameParameters?.classRoster) {
    try {
      const parsedList = JSON.parse(props.settings.gameParameters.classRoster);
      if (Array.isArray(parsedList) && parsedList.length > 0) {
        console.log(`[GameSettings] ✓ 從 props 載入名單 (${parsedList.length} 位)`);
        memberList.value = parsedList;
        useHiTeachRoster.value = true;
        rosterLoaded = true;

      }
    } catch (error) {
      console.error('[GameSettings] 解析 props 失敗:', error);
    }
  }

  // 檢查 2: 從 gameParameters.value 載入（本地備用）
  if (!rosterLoaded && gameParameters.value?.classRosterSource === 'HT' && gameParameters.value?.classRoster) {
    try {
      const parsedList = JSON.parse(gameParameters.value.classRoster);
      if (Array.isArray(parsedList) && parsedList.length > 0) {
        console.log(`[GameSettings] ✓ 從 gameParameters 載入名單 (${parsedList.length} 位)`);
        memberList.value = parsedList;
        useHiTeachRoster.value = true;
        rosterLoaded = true;

        // 同步到 sessionStorage（僅 WebView 環境）
        if (isWebview.value) {
          sessionStorage.setItem('ht_memberList', JSON.stringify(parsedList));
        }
      }
    } catch (error) {
      console.error('[GameSettings] 解析 gameParameters 失敗:', error);
    }
  }

  console.log('[GameSettings] 初始化完成:', {
    載入成功: rosterLoaded,
    名單長度: memberList.value.length,
    已勾選: useHiTeachRoster.value,
    環境: isWebview.value ? 'WebView' : '瀏覽器'
  });
};

onMounted(() => {
  // 確保是非題一開始就選中火箭升空
  if (props.questionType === 'trueFalse' && localSettings.currentGame !== 'tf') {
    localSettings.currentGame = 'tf';
    localSettings.allowTemplateChange = false;
  }

  // 確保單選題一開始就選中迷宮
  if (props.questionType === 'multiChoice' && localSettings.currentGame !== 'maze') {
    localSettings.currentGame = 'maze';
    localSettings.allowTemplateChange = false;
  }

  // HT 名單初始化
  console.log('[GameSettings] 開始初始化名單...');

  // 使用 setTimeout 確保在下一個事件循環中執行，讓 props 完全準備好
  setTimeout(() => {
    initRoster();
  }, 0);
});

// 監聽 props.settings 參考變化（例如切換不同活動時），重新初始化名單
// 不使用 deep，避免與本地 gameParameters 更新造成遞迴觸發
watch(() => props.settings, () => {
  console.log('[GameSettings] props.settings 變更，重新初始化名單');
  initRoster();
});

// 返回上一頁
const handleBack = () => {
  // 隱藏步驟二引導
  if (guideStep2Ref.value) {
    guideStep2Ref.value.hideGuide();
  }

  // 驗證輸入框是否有未提交的內容
  if (gameParametersFormRef.value && !gameParametersFormRef.value.validateInputs()) {
    return; // 驗證失敗，不執行返回
  }

  // 驗證必填欄位
  if (gameParametersFormRef.value && !gameParametersFormRef.value.validateRequiredFields()) {
    return; // 驗證失敗，不執行返回
  }

  emit('back');
};

// 遊戲預覽
const handlePreview = () => {
  // 自定義遊戲：更新 iframe 預覽
  if (props.questionType === 'aiGenerated') {
    const customCode = gameParameters.value?.customCode || '';
    if (customCode) {
      // 使用 gameSanitizer 檢查 HTML (只檢查不修改)
      const checkResult = sanitizeGameHTML(customCode);

      // 開發環境:記錄檢查結果
      if (process.env.NODE_ENV === 'development') {
        console.log('[自定義遊戲預覽] 安全檢查:', {
          程式碼大小: checkResult.stats.originalSize,
          檢查時間: checkResult.stats.duration + 'ms',
          發現問題數: checkResult.stats.issueCount,
          安全狀態: checkResult.safe ? '✓ 安全' : '⚠ 有警告'
        });

        if (checkResult.issues.length > 0) {
          console.warn('[自定義遊戲預覽] 發現潛在問題:', checkResult.issues);
        }
      }

      // 所有環境:若有問題則顯示自訂樣式警告 Modal（但預覽仍會照常進行）
      if (checkResult.issues.length > 0) {
        const errors = checkResult.issues.filter(i => i.severity === 'error');
        const warnings = checkResult.issues.filter(i => i.severity === 'warning');
        openSecurityWarningModal(errors, warnings);
      }

      // 使用原始 HTML (gameSanitizer 只檢查不修改)
      // iframe sandbox="allow-scripts" 提供隔離保護
      previewHtml.value = checkResult.clean;
    } else {
      previewHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 16px;">請輸入程式碼後點擊預覽</div>';
    }
    return;
  }

  // 一般遊戲：原有的預覽邏輯
  // 驗證輸入框
  if (gameParametersFormRef.value && !gameParametersFormRef.value.validateInputs()) {
    return; // 驗證失敗，不執行預覽
  }

  // 🐍 調試: 檢查 gameParameters.value 的完整內容
  console.log('🔍 [handlePreview] gameParameters.value:', JSON.parse(JSON.stringify(gameParameters.value)));

  // 收集當前設定參數（完整的 gameParameters）
  const previewData = {
    playerCount: localSettings.playerCount,
    currentGame: localSettings.currentGame,
    allowTemplateChange: localSettings.allowTemplateChange,
    questionType: props.questionType,
    gameParameters: {
      topic: gameParameters.value.topic || '',
      vocabularyList: gameParameters.value.vocabularyList || [],
      errorVocabularyList: gameParameters.value.errorVocabularyList || [],
      gameDuration: gameParameters.value.gameDuration || 30,
      background: gameParameters.value.background || (localSettings.currentGame === 'mole' ? 'forest' : 'ocean'),
      moleSpeed: gameParameters.value.moleSpeed || 2500, // 打地鼠速度
      gameSpeed: gameParameters.value.gameSpeed || 2, // 摘星星速度
      questionList: gameParameters.value.questionList || [], // 是非題題目列表
      answerMode: gameParameters.value.answerMode || 'sync', // 是非題答題模式
      // 🐍 貪食蛇特有參數
      difficulty: gameParameters.value.difficulty || 'medium',
      boardSize: gameParameters.value.boardSize || 20,
      snakeSpeed: gameParameters.value.snakeSpeed || 200,
      foodCount: gameParameters.value.foodCount || 8,
      initialSnakeLength: gameParameters.value.initialSnakeLength || 3
    }
  };

  // 🆕 調試日誌（所有環境都顯示）
  console.log('[預覽] 準備寫入 localStorage:', {
    currentGame: previewData.currentGame,
    playerCount: previewData.playerCount,
    dataSize: JSON.stringify(previewData).length
  });

  // 將預覽參數存入 localStorage（暫時存放）
  try {
    localStorage.setItem('game_preview_data', JSON.stringify(previewData));
    localStorage.setItem('game_preview_timestamp', Date.now().toString());

    // 🆕 驗證寫入是否成功
    const savedData = localStorage.getItem('game_preview_data');
    if (!savedData) {
      console.error('[預覽] localStorage 寫入失敗');
      return;
    }

    console.log('[預覽] localStorage 寫入成功，資料長度:', savedData.length);
  } catch (error) {
    console.error('[預覽] localStorage 寫入錯誤:', error);
    return;
  }

  // 根據遊戲類型決定路由路徑
  let previewPath = '';
  if (localSettings.currentGame === 'mole') {
    // 打地鼠
    previewPath = '/ht-molegame-play';
  } else if (localSettings.currentGame === 'star') {
    // 摘星星
    previewPath = '/ht-pvpgame-play';
  } else if (localSettings.currentGame === 'tf') {
    // 火箭升空（是非題）
    previewPath = '/ht-pvp-tfgame-play';
  } else if (localSettings.currentGame === 'snake') {
    // 🐍 貪食蛇
    previewPath = '/ht-snakegame-play';
  } else if (localSettings.currentGame === 'maze') {
    // 🧩 迷宮（單選題）
    previewPath = '/ht-mazegame-play';
  }

  // 🆕 延遲開啟新分頁或 iframe 彈窗，確保 localStorage 完全寫入
  // 在部署環境中，立即開啟新分頁可能導致 localStorage 未及時同步
  if (previewPath) {
    setTimeout(() => {
      // WebView 環境：使用 iframe 彈窗開啟（不離開當前頁面）
      if (isWebview.value) {
        console.log('[預覽] WebView 環境，使用 iframe 彈窗:', previewPath);
        openGamePreviewModal(previewPath);
      } else {
        // 瀏覽器環境：開啟新分頁（原有行為）
        const newWindow = window.open(previewPath, '_blank');

        if (!newWindow) {
          console.error('[預覽] 瀏覽器阻止了彈出視窗，請檢查瀏覽器設定');
        } else {
          console.log('[預覽] 新分頁已開啟:', previewPath);
        }
      }
    }, 100); // 100ms 延遲，確保 localStorage 同步
  } else {
    console.error('無法識別遊戲類型:', localSettings.currentGame);
  }
};
</script>

<style lang="less" scoped>
@import "../../assets/color.less";
@import "../../assets/gameActManager.less";

.settings-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
  z-index: 10000;
  display: flex;
  flex-direction: column;
}

// 標題列
.settings-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 600;
  color: @game-primary-color;
  background-color: @game-active-color;
  border: 1px solid @game-primary-color;
  border-radius: 4px;
}

.back-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: @game-primary-color;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  width: 28px;
  height: 28px;

  &:hover {
    color: @game-hover-color;
  }

  :deep(svg) {
    width: 20px;
    height: 20px;
    fill: @game-primary-color;
    stroke: @game-primary-color;
  }

  &:hover :deep(svg) {
    fill: @game-hover-color;
    stroke: @game-hover-color;
  }
}

.settings-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.2;
}

// 設定內容
.settings-body {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

// 布局：桌面版左右分欄，手機版單欄
.settings-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  height: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    height: auto;
  }
}

.settings-left {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.settings-right {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-right: 8px;

  @media (max-width: 768px) {
    overflow-y: visible;
    padding-right: 0;
  }

  // 捲軸樣式
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;

    &:hover {
      background: #555;
    }
  }
}

.setting-group {
  margin-bottom: 0;

  &.allow-change-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .setting-label {
      margin-bottom: 0;
    }
  }
}

.setting-label {
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  line-height: 1.6;
}

// Radio 按鈕組
.radio-group {
  display: flex;
  gap: 24px;
}

.radio-item {
  display: flex;
  align-items: center;
  cursor: pointer;

  input[type="radio"] {
    width: 20px;
    height: 20px;
    margin-right: 8px;
    cursor: pointer;
    accent-color: @game-primary-color;
  }

  .radio-text {
    font-size: 16px;
    color: #333;
    line-height: 1.6;

    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

// 遊戲模板圖像卡牌網格
.game-template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }
}

.game-template-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
  background-color: white;
  border: 3px solid transparent;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }

  &.active {
    border-color: @game-primary-color;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .game-card-image {
    width: 100%;
    height: 100px;
    overflow: hidden;
    background-color: #f5f5f5;

    @media (max-width: 768px) {
      height: 80px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  .game-card-name {
    padding: 10px;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    color: #333;
    line-height: 1.4;

    @media (max-width: 768px) {
      padding: 8px;
      font-size: 13px;
    }
  }
}

// Toggle Switch
.toggle-switch {
  display: inline-flex;
  align-items: center;
}

.toggle-input {
  display: none;
}

.toggle-label {
  position: relative;
  width: 50px;
  height: 28px;
  background-color: #d9d9d9;
  border-radius: 14px;
  cursor: pointer;
  transition: background-color 0.3s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 24px;
    height: 24px;
    background-color: white;
    border-radius: 50%;
    transition: transform 0.3s;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.toggle-input:checked + .toggle-label {
  background-color: @game-primary-color;
}

.toggle-input:checked + .toggle-label::after {
  transform: translateX(22px);
}

.toggle-input:disabled + .toggle-label {
  cursor: not-allowed;
}

.setting-label.disabled {
  opacity: 0.5;
  color: #999;
}

// 設定說明文字
.setting-note {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

// 預覽按鈕
.btn-preview {
  width: 100%;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 700;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1.6;
  background-color: @game-primary-color;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .preview-icon {
    width: 20px;
    height: 20px;

    :deep(svg) {
      fill: white;
      stroke: white;
    }
  }

  &:hover {
    background-color: @game-hover-color;
  }

  &:active {
    transform: translateY(1px);
  }
}

// 自定義遊戲專用佈局
.custom-game-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  height: calc(100vh - 140px);
  overflow: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .code-editor-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow: hidden;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;

      .section-title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        line-height: 1.6;
      }

      .btn-run {
        padding: 8px 32px;
        font-size: 16px;
        font-weight: 700;
        background-color: @game-primary-color;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s;
        line-height: 1.6;
        flex-shrink: 0;

        &:hover {
          background-color: @game-hover-color;
        }

        &:active {
          transform: translateY(1px);
        }
      }
    }
  }

  .preview-section {
    display: flex;
    flex-direction: column;
    background-color: #f5f5f5;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #d0d0d0; // 四邊灰色框線

    .preview-container {
      flex: 1;
      position: relative;
      background-color: #ffffff;
      overflow: auto; // 啟用捲動

      .preview-iframe {
        width: 100%;
        height: 100%;
        border: none;
        display: block;
        background-color: #ffffff;
      }
    }
  }
}

// ========== HT 名單區域樣式 ==========

// HT 名單區域（位於 settings-left 內，預覽按鈕下方）
.roster-section {
  margin-top: 24px;
  margin-bottom: 24px; // 加入下邊距，避免貼近下緣
  padding-top: 24px;
  border-top: 1px solid #e8e8e8;
  width: 100%;
}

.roster-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

// 勾選框樣式
.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  .roster-checkbox {
    width: 20px;
    height: 20px;
    margin-right: 8px;
    cursor: pointer;
    accent-color: @game-primary-color;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .checkbox-label {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    line-height: 1.6;
  }

  // 禁用狀態樣式（非 WebView 環境）
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;

    .checkbox-label {
      color: #999;
    }
  }

  // 使用 :has 選擇器作為備用（較新的瀏覽器支援）
  &:has(.roster-checkbox:disabled) {
    opacity: 0.7;

    .checkbox-label {
      color: #999;
    }
  }
}

// 重新整理按鈕
.btn-refresh-roster {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: @game-primary-color;
  background-color: white;
  border: 1px solid @game-primary-color;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1.6;

  &:hover {
    background-color: @game-active-color;
    border-color: @game-hover-color;
    color: @game-hover-color;
  }

  &:active {
    transform: translateY(1px);
  }

  :deep(svg) {
    width: 16px;
    height: 16px;
  }
}

// 名單顯示區域
.roster-display {
  margin-top: 12px;
}

.roster-title {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.6;

  .student-count {
    color: @game-primary-color;
    font-weight: 700;
  }
}

.roster-table-wrapper {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 6px;

  // 捲軸樣式
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;

    &:hover {
      background: #555;
    }
  }
}

.roster-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    position: sticky;
    top: 0;
    background-color: #f5f5f5;
    z-index: 1;

    th {
      padding: 10px 12px;
      text-align: left;
      font-size: 14px;
      font-weight: 600;
      color: #333;
      border-bottom: 2px solid #e8e8e8;
      line-height: 1.6;

      &:first-child {
        width: 80px;
      }
    }
  }

  tbody {
    tr {
      &:hover {
        background-color: #f9f9f9;
      }

      &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
      }
    }

    td {
      padding: 10px 12px;
      font-size: 14px;
      color: #666;
      line-height: 1.6;
      min-height: 20px; // 確保即使內容為空也有高度
      white-space: normal; // 確保文字正常顯示
      word-break: break-word; // 長文字換行

      &:first-child {
        font-weight: 600;
        color: @game-primary-color;
        text-align: center; // 座號置中
        width: 80px;
      }

      &:nth-child(2) {
        text-align: left; // 姓名靠左
      }
    }
  }
}

// 名單為空提示
.roster-empty {
  padding: 24px;
  text-align: center;
  background-color: #f9f9f9;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  margin-top: 12px;

  .info-icon {
    width: 32px;
    height: 32px;
    margin-bottom: 12px;
    opacity: 0.6;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: #999;
    }
  }

  p {
    font-size: 14px;
    color: #999;
    margin-bottom: 16px;
    line-height: 1.6;
  }

  .btn-refresh-roster-secondary {
    padding: 8px 24px;
    font-size: 14px;
    font-weight: 600;
    color: white;
    background-color: @game-primary-color;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    line-height: 1.6;

    &:hover {
      background-color: @game-hover-color;
    }

    &:active {
      transform: translateY(1px);
    }
  }
}

// 響應式設計
@media (max-width: 768px) {
  .roster-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .btn-refresh-roster {
    width: 100%;
    justify-content: center;
  }

  .roster-table-wrapper {
    max-height: 200px;
  }

  .roster-section {
    margin-top: 20px;
  }
}

@media (min-width: 769px) {
  .roster-table-wrapper {
    max-height: 250px;
  }

  .btn-refresh-roster {
    flex: 1;
    min-width: 140px;
  }
}

// ========== 遊戲預覽 iframe 彈窗樣式 (WebView 專用) ==========

.game-preview-overlay {
  z-index: 20000; // 確保在所有內容之上
  background-color: rgba(0, 0, 0, 0.7); // 較深的遮罩背景
}

.game-preview-modal {
  position: fixed; // 固定定位，用於螢幕居中
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 95vw;
  height: 95vh;
  max-width: 1600px;
  max-height: none; // 移除最大高度限制，使用視窗高度
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;

  @media (max-width: 768px) {
    width: 98vw;
    height: 98vh;
    border-radius: 8px;
  }

  // 為內部元素提供相對定位上下文（不影響自身定位）
  & > * {
    position: relative;
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translate(-50%, -48%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

// iframe 直接撐滿整個彈窗
.game-preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background-color: white;
  border-radius: 12px;

  @media (max-width: 768px) {
    border-radius: 8px;
  }
}

// 標題欄浮動在 iframe 上方（完全透明）
.preview-modal-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: transparent; // 完全透明背景
  color: white;
  z-index: 10; // 確保在 iframe 上方
  pointer-events: none; // 讓透明區域不阻擋點擊

  @media (max-width: 768px) {
    padding: 12px 16px;
  }

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.4;
    color: #ff9234; // 橘色字體
    text-shadow: none; // 移除陰影，避免干擾
    pointer-events: none;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  .close-btn {
    background: rgba(0, 0, 0, 0.3); // 半透明黑色背景
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
    pointer-events: auto; // 恢復按鈕的點擊功能
    backdrop-filter: blur(4px); // 輕微模糊效果

    &:hover {
      background-color: rgba(0, 0, 0, 0.5);
      transform: scale(1.05);
    }

    :deep(svg) {
      width: 18px;
      height: 18px;
      fill: white;
      stroke: white;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5)); // 為圖標加陰影
    }

    @media (max-width: 768px) {
      width: 28px;
      height: 28px;

      :deep(svg) {
        width: 16px;
        height: 16px;
      }
    }
  }
}
</style>
